import React, { useEffect, useRef, useState } from 'react';
import {
  Box, Grid, Typography,
} from '@mui/material';
import { Business, Email, Phone, LocationOn } from '@mui/icons-material';
import { Checkbox, DatePicker, Form as AntForm, Input as AntInput, InputNumber } from 'antd';
import dayjs from 'dayjs';
import axios from 'axios';
import { useAuthHeader } from '../../Services/Auth/sessionAuth';
import { encryptBody, mfaApi, parseEncryptedResponse } from './mfaApi';
import type { MfaActionMode } from './MfaPage';
import Swal from 'sweetalert2';
import SearchModal from '../../Components/SearchModal';
import ParamsPicklist from '../../Types/ParamsPicklist';

export type Societe = {
  CODSTE: string;
  RAISON: string;
  STATUT: string;
  TYPESTE?: string | null;
  TYPE_SOCIETE?: string | null;
  ACTIVITEP?: string | null;
  ACTIVITEA?: string | null;
  SECTEUR?: string | null;
  categoEmf?: number | null;
  CHIFFREA?: number | null;
  BP?: string | null;
  TEL?: string | null;
  FAX?: string | null;
  EMAIL?: string | null;
  LOCAL?: string | null;
  CONTRIB?: string | null;
  REGISTRE?: string | null;
  DATNAI?: string | null;
  codpays?: string | null;
  coddev?: string | null;
  FORMATCPT: string;
  formatCptCli?: string | null;
  formatCptGl?: string | null;
  AUTFORMATLIBRE?: boolean;
};

const emptySociete: Societe = {
  CODSTE: '',
  RAISON: '',
  STATUT: '',
  TYPESTE: '',
  ACTIVITEP: '',
  ACTIVITEA: '',
  SECTEUR: '',
  categoEmf: null,
  CHIFFREA: null,
  BP: '',
  TEL: '',
  FAX: '',
  EMAIL: '',
  LOCAL: '',
  CONTRIB: '',
  REGISTRE: '',
  DATNAI: '',
  codpays: 'CM',
  coddev: 'XAF',
  FORMATCPT: '',
  formatCptCli: '',
  formatCptGl: '',
  AUTFORMATLIBRE: false,
};

function getVal(raw: any, ...keys: string[]) {
  for (const key of keys) {
    if (raw[key] !== undefined && raw[key] !== null) return raw[key];
  }
  return undefined;
}

function toBool(value: any): boolean {
  return value === true || value === 1 || String(value).trim().toLowerCase() === 'true' || String(value).trim() === '1';
}

function getFormatCptParts(raw: any) {
  const json = String(getVal(raw, 'FORMATCPT', 'FormatCpt', 'formatcpt') ?? '');
  if (!json) return { formatCptCli: '', formatCptGl: '' };

  try {
    const parsed = JSON.parse(json);
    return {
      formatCptCli: String(parsed.formatCptCli ?? parsed.formatcptcli ?? ''),
      formatCptGl: String(parsed.formatcptGl ?? parsed.formatcptgl ?? ''),
    };
  } catch {
    return { formatCptCli: '', formatCptGl: '' };
  }
}

function normalizeSociete(raw: any): Societe {
  const parts = getFormatCptParts(raw);
  return {
    CODSTE: getVal(raw, 'CODSTE', 'CodSte', 'codste', 'code_societe', 'CodeSociete') ?? '',
    RAISON: getVal(raw, 'RAISON', 'Raison', 'raison', 'RAISONSOC', 'RaisonSoc') ?? '',
    STATUT: getVal(raw, 'STATUT', 'Statut', 'statut', 'ETAT', 'Etat') ?? 'ACT',
    TYPESTE: getVal(raw, 'TYPESTE', 'TypeSte', 'typeste', 'TYPE_SOCIETE', 'TypeSociete') ?? 'SA',
    FORMATCPT: String(getVal(raw, 'FORMATCPT', 'FormatCpt', 'formatcpt') ?? ''),
    formatCptCli: parts.formatCptCli,
    formatCptGl: parts.formatCptGl,
    ACTIVITEP: getVal(raw, 'ACTIVITEP', 'ActiviteP', 'activitep', 'ACTIVITE_P', 'activite_p', 'CODNAEP', 'CODE_ACTIVITE_P', 'ACTIVITE_PRIMAIRE') ?? '',
    ACTIVITEA: getVal(raw, 'ACTIVITEA', 'ActiviteA', 'activitea', 'ACTIVITE_A', 'activite_a', 'CODNAA', 'CODE_ACTIVITE_A', 'ACTIVITE_SECONDAIRE') ?? '',
    SECTEUR: getVal(raw, 'SECTEUR', 'Secteur', 'secteur', 'CODE_SECTEUR', 'code_secteur', 'CODSEC', 'SECTEUR_LIBELLE') ?? '',
    categoEmf: Number(getVal(raw, 'categoEmf', 'CategoEmf', 'CODE_CATEGORIE', 'code_categorie', 'CATEGORIE') ?? 0) || 0,
    CHIFFREA: Number(getVal(raw, 'CHIFFREA', 'ChiffreA', 'chiffre_affaires', 'CHIFFRE_AFFAIRES', 'CHAFF') ?? 0),
    DATNAI: getVal(raw, 'DATNAI', 'DatNai', 'datnai', 'DATE_CREATION', 'date_creation', 'DATE_NAISSANCE', 'DATECR') ?? '',
    BP: getVal(raw, 'BP', 'Bp', 'bp', 'BOITE_POSTALE', 'boite_postale') ?? '',
    TEL: getVal(raw, 'TEL', 'Tel', 'tel', 'TELEPHONE', 'telephone') ?? '',
    FAX: getVal(raw, 'FAX', 'Fax', 'fax') ?? '',
    EMAIL: getVal(raw, 'EMAIL', 'Email', 'email') ?? '',
    LOCAL: getVal(raw, 'LOCAL', 'Local', 'local', 'LOCALISATION', 'localisation', 'ADRESSE', 'adresse') ?? '',
    CONTRIB: getVal(raw, 'CONTRIB', 'Contrib', 'contrib', 'NUMCONTRIB', 'numcontrib', 'CONTRIBUABLE') ?? '',
    REGISTRE: getVal(raw, 'REGISTRE', 'Registre', 'registre', 'REGISTRE_COMMERCE', 'registre_commerce', 'RC') ?? '',
    codpays: getVal(raw, 'codpays', 'CODPAYS', 'CodPays', 'PAYS', 'pays') ?? 'CM',
    coddev: getVal(raw, 'coddev', 'CODDEV', 'CodDev', 'DEVISE', 'devise') ?? 'XAF',
    AUTFORMATLIBRE: toBool(getVal(raw, 'AUTFORMATLIBRE', 'AutFormatLibre', 'autformatlibre')),
  };
}

function resolveReferenceCode(
  value: unknown,
  options: { value: any; label: string }[],
) {
  const text = String(value ?? '').trim();
  const match = options.find((option) =>
    String(option.value).trim() === text || option.label.trim().toLowerCase() === text.toLowerCase(),
  );
  return match?.value ?? value;
}

const STATUT_OPTIONS = [
  { value: 'ACT', label: 'Actif' },
  { value: 'INACT', label: 'Inactif' },
];

const PAYS_OPTIONS = [
  { value: 'CM', label: 'Cameroun' },
];

const DEVISE_OPTIONS = [
  { value: 'XAF', label: 'XAF — Franc CFA (CEMAC)' },
];

type FieldErrors = Partial<Record<keyof Societe, string>>;

interface SocieteAdminPanelProps {
  mode?: MfaActionMode;
  saveRequest?: number;
  deleteRequest?: number;
  consultRequest?: number;
  onSaved?: () => void;
  selectedSociete?: Societe | null;
  onSelectSociete?: (societe: Societe | null) => void;
  searchForm: any;
}

export default function SocieteAdminPanel({
  mode = 'view',
  saveRequest = 0,
  deleteRequest = 0,
  consultRequest = 0,
  onSaved,
  selectedSociete,
  onSelectSociete,
  searchForm,
}: SocieteAdminPanelProps) {
  const authHeader = useAuthHeader();
  const headers = { Authorization: authHeader(), 'Content-Type': 'application/json' };

  const [societes, setSocietes] = useState<Societe[]>([]);
  const [form, setForm] = useState<Societe>(emptySociete);
  const [errors, setErrors] = useState<FieldErrors>({});
  const [editing, setEditing] = useState(false);
  const [busy, setBusy] = useState(false);
  const formRef = useRef<HTMLFormElement>(null);

  const [typeSocieteOptions, setTypeSocieteOptions] = useState<{ value: string; label: string }[]>([]);
  const [activiteOptions, setActiviteOptions] = useState<{ value: string; label: string }[]>([]);
  const [secteurOptions, setSecteurOptions] = useState<{ value: string; label: string }[]>([]);
  const [categorieOptions, setCategorieOptions] = useState<{ value: number; label: string }[]>([]);
  const [formatCompteOptions, setFormatCompteOptions] = useState<{ value: string; label: string }[]>([]);

  const searchSocieteParam: ParamsPicklist = {
    CodeAgence: '',
    Table: 'SOCIETE',
    Colonne1: 'CODSTE',
    Colonne2: 'RAISON',
    Colagence: '',
    Choix: 22,
  };

  const searchActiviteParam: ParamsPicklist = {
    CodeAgence: '',
    Table: 'TABNAE',
    Colonne1: 'CODNAE',
    Colonne2: 'LIBELL',
    Colagence: '101',
    Choix: 22,
  };

  const searchSecteurParam: ParamsPicklist = {
    CodeAgence: 'SECTEUR',
    Table: 'LIB',
    Colonne1: 'CODLIB',
    Colonne2: 'LIBELL',
    Colagence: 'RUBRIQ',
    Choix: 22,
  };

  const searchCategorieEmfParam: ParamsPicklist = {
    CodeAgence: '',
    Table: 'CATEGO',
    Colonne1: 'CATEGO',
    Colonne2: 'LIBELLFR',
    Colagence: '101',
    Choix: 22,
  };

  const searchStatutParam: ParamsPicklist = {
    CodeAgence: '', Table: 'Status', Colonne1: 'CODLIB', Colonne2: 'LIBELL', Colagence: '101', Choix: 22,
  };

  const searchTypeSocieteParam: ParamsPicklist = {
    CodeAgence: '', Table: 'TYPESOCIETE', Colonne1: 'CODTYPE', Colonne2: 'LIBELLE', Colagence: '101', Choix: 22,
  };

  const searchPaysParam: ParamsPicklist = {
    CodeAgence: '', Table: 'PAYS', Colonne1: 'CODPAYS', Colonne2: 'LIBELLE', Colagence: '101', Choix: 22,
  };

  const searchDeviseParam: ParamsPicklist = {
    CodeAgence: '', Table: 'DEVISE', Colonne1: 'CODDEV', Colonne2: 'LIBELLE', Colagence: '101', Choix: 22,
  };

  function selectSocieteFromSearch(code: string) {
    const local = societes.find((societe) => societe.CODSTE === code);
    onSelectSociete?.(local ? hydrateSociete(local) : { ...emptySociete, CODSTE: code });
  }

  function selectActivitePFromSearch(value: string) {
    const byCode = activiteOptions.find((o) => o.value === value);
    const byLabel = activiteOptions.find((o) => o.label === value);
    update('ACTIVITEP', byCode?.value ?? byLabel?.value ?? value);
  }

  function selectActiviteAFromSearch(value: string) {
    const byCode = activiteOptions.find((o) => o.value === value);
    const byLabel = activiteOptions.find((o) => o.label === value);
    update('ACTIVITEA', byCode?.value ?? byLabel?.value ?? value);
  }

  function selectSecteurFromSearch(value: string) {
    const byCode = secteurOptions.find((o) => o.value === value);
    const byLabel = secteurOptions.find((o) => o.label === value);
    update('SECTEUR', byCode?.value ?? byLabel?.value ?? value);
  }

  function selectCategorieEmfFromSearch(value: string) {
    const byCode = categorieOptions.find((o) => String(o.value) === value);
    const byLabel = categorieOptions.find((o) => o.label === value);
    const code = byCode?.value ?? byLabel?.value ?? Number(value);
    update('categoEmf', Number.isFinite(code) && Number(code) > 0 ? Number(code) : null);
  }

  const formLocked = mode === 'view';

  function unwrap(response: any) {
    const payload = parseEncryptedResponse(response);
    return payload?.Result ?? payload?.result ?? payload;
  }

  function hydrateSociete(raw: any): Societe {
    const societe = normalizeSociete(raw);
    return {
      ...societe,
      STATUT: String(resolveReferenceCode(societe.STATUT, STATUT_OPTIONS)),
      TYPESTE: String(resolveReferenceCode(societe.TYPESTE, typeSocieteOptions)),
      ACTIVITEP: String(resolveReferenceCode(societe.ACTIVITEP, activiteOptions)),
      ACTIVITEA: String(resolveReferenceCode(societe.ACTIVITEA, activiteOptions)),
      SECTEUR: String(resolveReferenceCode(societe.SECTEUR, secteurOptions)),
      categoEmf: Number(resolveReferenceCode(
        societe.categoEmf && societe.categoEmf > 0
          ? societe.categoEmf
          : getVal(raw, 'CATEGORIE_EMF', 'CATEGORIE', 'CODE_CATEGORIE', 'code_categorie', 'categoEmf'),
        categorieOptions,
      )) || 0,
      codpays: String(resolveReferenceCode(societe.codpays, PAYS_OPTIONS)),
      coddev: String(resolveReferenceCode(societe.coddev, DEVISE_OPTIONS)),
      formatCptCli: String(resolveReferenceCode(societe.formatCptCli, formatCompteOptions)),
      formatCptGl: String(resolveReferenceCode(societe.formatCptGl, formatCompteOptions)),
    };
  }

  async function load() {
    try {
      const response = await axios.get(mfaApi.societeAll, { headers, withCredentials: true });

      const payload = parseEncryptedResponse(response);

      const data = payload?.Result ?? payload?.result ?? payload;
      const list = Array.isArray(data)
        ? data
        : data?.Table ?? data?.table ?? data?.Items ?? data?.items ?? [];
      const normalizedList = Array.isArray(list) ? list.map(hydrateSociete) : [];

      setSocietes(normalizedList);
    } catch (error: any) {

      Swal.fire({
        icon: 'error',
        title: 'Erreur',
        text: unwrap(error?.response)?.Message ?? 'Impossible de charger les sociétés.',
        confirmButtonColor: '#0d4a70',
      });
    }
  }

  async function loadReferences() {
    const fetchList = async (url: string, map: (row: any) => { value: any; label: string }) => {
      const data = unwrap(await axios.get(url, { headers, withCredentials: true }));
      const rows = Array.isArray(data)
        ? data
        : data?.Table ?? data?.table ?? data?.Items ?? data?.items ?? [];
      return Array.isArray(rows) ? rows.map(map) : [];
    };
    try {
      setTypeSocieteOptions(await fetchList(mfaApi.typeSocieteAll, (r) => ({
        value: r.code_type ?? r.CODE_TYPE ?? '',
        label: r.libelle ?? r.LIBELLE ?? r.code_type ?? '',
      })));
      setActiviteOptions(await fetchList(mfaApi.activiteAll, (r) => ({
        value: r.code_activite ?? r.CODE_ACTIVITE ?? '',
        label: r.libelle ?? r.LIBELLE ?? r.code_activite ?? '',
      })));
      setSecteurOptions(await fetchList(mfaApi.secteurActiviteAll, (r) => ({
        value: getVal(r, 'code_secteur', 'CODE_SECTEUR', 'codsecteur', 'CODSECTEUR', 'CODSEC', 'code', 'CODE') ?? '',
        label: getVal(r, 'libelle', 'LIBELLE', 'libelle_secteur', 'LIBELLE_SECTEUR', 'LIBSEC', 'libellefr', 'LIBELLFR') ?? getVal(r, 'code_secteur', 'CODE_SECTEUR', 'CODSEC', 'code', 'CODE') ?? '',
      })));
      setCategorieOptions(await fetchList(mfaApi.categorieEmfAll, (r) => ({
        value: Number(r.code_categorie ?? r.CODE_CATEGORIE ?? 0),
        label: r.libelle ?? r.LIBELLE ?? String(r.code_categorie),
      })));
      setFormatCompteOptions(await fetchList(mfaApi.formatCompteAll, (r) => ({
        value: String(getVal(r, 'idFormat', 'IdFormat', 'IDFORMAT') ?? ''),
        label: String(getVal(r, 'nomFormat', 'NomFormat', 'NOMFORMAT') ?? ''),
      })));
    } catch (error: any) {
    }
  }

  useEffect(() => { load(); loadReferences(); }, []);

  function update(field: keyof Societe, value: string | number | boolean | null) {
    setForm((current) => ({ ...current, [field]: value }));
    if (errors[field]) setErrors((current) => ({ ...current, [field]: undefined }));
  }

  useEffect(() => {
    if (mode === 'add') {
      setForm(emptySociete);
      setErrors({});
      setEditing(false);
      onSelectSociete?.(null);
    }
  }, [mode]);

  useEffect(() => {
    if (mode === 'edit' && selectedSociete) {
      setForm(selectedSociete);
      setErrors({});
      setEditing(true);
    }
  }, [mode, selectedSociete]);

  useEffect(() => {
    if (mode === 'view') {
      setEditing(false);
      setErrors({});
    }
  }, [mode]);

  useEffect(() => {
    if (consultRequest > 0 && selectedSociete?.CODSTE) {
      const consult = async () => {
        try {
          const response = await axios.get(mfaApi.societeAll, { headers, withCredentials: true });
          const payload = parseEncryptedResponse(response);
          const data = payload?.Result ?? payload?.result ?? payload;
          const rows = Array.isArray(data) ? data : data?.Table ?? data?.table ?? data?.Items ?? data?.items ?? [];
          const raw = Array.isArray(rows)
            ? rows.find((societe: any) => String(getVal(societe, 'CODSTE', 'CodSte', 'codste') ?? '') === String(selectedSociete.CODSTE))
            : null;
          if (!raw) throw new Error('Société introuvable.');

          const consulted = hydrateSociete(raw);
          searchForm.setFieldsValue({
            CODSTE_SEARCH: consulted.CODSTE,
            lib_CODSTE_SEARCH: consulted.RAISON,
            STATUT_SEARCH: consulted.STATUT,
            lib_STATUT_SEARCH: STATUT_OPTIONS.find((option) => option.value === consulted.STATUT)?.label ?? consulted.STATUT,
            TYPESTE_SEARCH: consulted.TYPESTE,
            lib_TYPESTE_SEARCH: typeSocieteOptions.find((option) => option.value === consulted.TYPESTE)?.label ?? consulted.TYPESTE,
            ACTIVITEP_SEARCH: consulted.ACTIVITEP,
            lib_ACTIVITEP_SEARCH: activiteOptions.find((option) => option.value === consulted.ACTIVITEP)?.label ?? consulted.ACTIVITEP,
            ACTIVITEA_SEARCH: consulted.ACTIVITEA,
            lib_ACTIVITEA_SEARCH: activiteOptions.find((option) => option.value === consulted.ACTIVITEA)?.label ?? consulted.ACTIVITEA,
            SECTEUR_SEARCH: consulted.SECTEUR,
            lib_SECTEUR_SEARCH: secteurOptions.find((option) => option.value === consulted.SECTEUR)?.label ?? consulted.SECTEUR,
            CATEGOEMF_SEARCH: consulted.categoEmf ? String(consulted.categoEmf) : '',
            lib_CATEGOEMF_SEARCH: categorieOptions.find((option) => option.value === consulted.categoEmf)?.label ?? '',
            PAYS_SEARCH: consulted.codpays,
            lib_PAYS_SEARCH: PAYS_OPTIONS.find((option) => option.value === consulted.codpays)?.label ?? consulted.codpays,
            DEVISE_SEARCH: consulted.coddev,
            lib_DEVISE_SEARCH: DEVISE_OPTIONS.find((option) => option.value === consulted.coddev)?.label ?? consulted.coddev,
            FORMATCPTCLI_SEARCH: consulted.formatCptCli,
            lib_FORMATCPTCLI_SEARCH: formatCompteOptions.find((o) => o.value === consulted.formatCptCli)?.label ?? consulted.formatCptCli,
            FORMATCPTGL_SEARCH: consulted.formatCptGl,
            lib_FORMATCPTGL_SEARCH: formatCompteOptions.find((o) => o.value === consulted.formatCptGl)?.label ?? consulted.formatCptGl,
          });
   
          setForm(consulted);
          setEditing(false);
          setErrors({});
        } catch (error: any) {
          Swal.fire({
            icon: 'error',
            title: 'Erreur',
            text: error?.message ?? 'Impossible de charger la société.',
            confirmButtonColor: '#0d4a70',
          });
        }
      };
      consult();
    }
  }, [consultRequest, selectedSociete?.CODSTE]);

  useEffect(() => {
    if (!selectedSociete && mode === 'view') {
      setForm({ ...emptySociete });
      searchForm.resetFields();
      setEditing(false);
      setErrors({});
    }
  }, [selectedSociete, mode, searchForm]);

  const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
  const PHONE_RE = /^[0-9+()\s-]{6,20}$/;

  // Seuls ces champs sont obligatoires.
  function validate(values: Societe): FieldErrors {
    const next: FieldErrors = {};

    if (!values.CODSTE?.trim()) next.CODSTE = 'Code société requis.';
    else if (!/^[A-Z0-9_-]{2,10}$/i.test(values.CODSTE)) next.CODSTE = '2 à 10 caractères alphanumériques.';

    if (!values.ACTIVITEP?.trim()) next.ACTIVITEP = 'Activité primaire requise.';
    if (!values.ACTIVITEA?.trim()) next.ACTIVITEA = 'Activité secondaire requise.';
    if (!values.SECTEUR?.trim()) next.SECTEUR = 'Secteur requis.';

    if (values.categoEmf === null || values.categoEmf === undefined) next.categoEmf = 'Catégorie EMF requise.';
    else if (values.categoEmf < 1) next.categoEmf = 'Doit être ≥ 1.';

    return next;
  }

  async function save(event: React.FormEvent) {
    event.preventDefault();

    const fieldErrors = validate(form);
    if (Object.keys(fieldErrors).length > 0) {
      setErrors(fieldErrors);
      Swal.fire({
        title: 'Veuillez remplir tous les champs obligatoires',
        text: Object.values(fieldErrors).join(' - '),
        icon: 'warning',
        confirmButtonText: 'OK',
      });
      return;
    }

    // Confirmation identique à la gestion utilisateur (ManageUser/User.tsx).
    const confirmation = await Swal.fire({
      title: `${editing ? 'Modification' : 'Enregistrement'} de la société`,
      text: `Voulez-vous ${editing ? 'modifier' : 'enregistrer'} la société ${form.CODSTE} ?`,
      showCancelButton: true,
      color: '#116599',
      confirmButtonText: 'Yes',
      confirmButtonColor: '#116599',
      cancelButtonColor: 'darkred',
      cancelButtonText: 'No',
      showLoaderOnConfirm: true,
      icon: 'question',
    });
    if (!confirmation.isConfirmed) return;

    setBusy(true);
    try {
      const url = editing ? mfaApi.societeUpdate : mfaApi.societeCreate;
      const body = { ...form, FORMATCPT: ''};

      delete body.formatCptCli;
      delete body.formatCptGl;
      
      body.FORMATCPT = JSON.stringify({
        formatCptCli: form.formatCptCli ?? '',
        formatcptGl: form.formatCptGl ?? '',
      });
      const response = await axios({ method: editing ? 'put' : 'post', url, data: encryptBody(body), headers, withCredentials: true });
      const payload = unwrap(response);
      if (response.status >= 200 && response.status < 300) {
        setForm(emptySociete);
        setErrors({});
        setEditing(false);
        onSelectSociete?.(null);
        await load();
        onSaved?.();
        Swal.fire({
          icon: 'success',
          title: 'Succès',
          text: editing ? 'Société mise à jour avec succès.' : 'Société créée avec succès.',
          confirmButtonColor: '#0d4a70',
        });
      } else {
        Swal.fire({
          icon: 'error',
          title: 'Erreur',
          text: payload?.Message ?? 'Opération Société refusée.',
          confirmButtonColor: '#0d4a70',
        });
      }
    } catch (error: any) {
      Swal.fire({
        icon: 'error',
        title: 'Erreur',
        text: unwrap(error?.response)?.Message ?? 'Opération Société refusée.',
        confirmButtonColor: '#0d4a70',
      });
    } finally {
      setBusy(false);
    }
  }

  useEffect(() => {
    if (saveRequest > 0 && mode !== 'view') formRef.current?.requestSubmit();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [saveRequest]);

  useEffect(() => {
    if (deleteRequest > 0 && selectedSociete) removeSociete(selectedSociete.CODSTE);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [deleteRequest]);

  async function removeSociete(codste: string) {
    const result = await Swal.fire({
      title: 'Suppression de la société',
      text: `Voulez-vous vraiment supprimer la société ${codste} ?`,
      showCancelButton: true,
      color: '#116599',
      confirmButtonText: 'Yes',
      confirmButtonColor: '#116599',
      cancelButtonColor: 'darkred',
      cancelButtonText: 'No',
      showLoaderOnConfirm: true,
      icon: 'question',
    });
    if (!result.isConfirmed) return;
    setBusy(true);
    try {
      const response = await axios.delete(mfaApi.societeDelete(codste), { headers, withCredentials: true });
      if (response.status >= 200 && response.status < 300) {
        onSelectSociete?.(null);
        await load();
        onSaved?.();
        Swal.fire({
          icon: 'success',
          title: 'Succès',
          text: 'Société supprimée avec succès.',
          confirmButtonColor: '#0d4a70',
        });
      }
    } catch (error: any) {
      Swal.fire({
        icon: 'error',
        title: 'Erreur',
        text: unwrap(error?.response)?.Message ?? 'Suppression refusée.',
        confirmButtonColor: '#0d4a70',
      });
    } finally {
      setBusy(false);
    }
  }

  // Style commun des libellés (charte : label à gauche, retour à la ligne autorisé)
  const LABEL_COL = { span: 8, style: { whiteSpace: 'pre-wrap' as const } };

  const REQUIRED_FIELDS: (keyof Societe)[] = ['CODSTE', 'ACTIVITEP', 'ACTIVITEA', 'SECTEUR', 'categoEmf'];

  // Props communes d'un champ : cadre "bordure" + affichage de l'erreur de validation
  const itemProps = (field: keyof Societe) => ({
    className: 'bordure',
    labelAlign: 'left' as const,
    labelCol: LABEL_COL,
    required: REQUIRED_FIELDS.includes(field),
    validateStatus: errors[field] ? ('error' as const) : undefined,
    help: errors[field],
  });

  const datnai = form.DATNAI ? dayjs(form.DATNAI.slice(0, 10)) : null;

  return (
    <div className="tabItem societe-form">
      <Box
        fontStyle="inherit"
        className="border"
        sx={{
          p: 1,
          width: '100%',
          maxHeight: '75vh',       // ajustez selon la hauteur de votre écran cible
          overflowY: 'auto',       // le scroll reste DANS le cadre du formulaire
          boxSizing: 'border-box',
        }}
      >
      {editing && (
        <legend>
          <Typography color="#116599">
            <Business sx={{ fontSize: 18, verticalAlign: 'text-bottom', mr: 1 }} />
            Modifier la société
          </Typography>
        </legend>
      )}

        {/* Recherche Société — SearchModal identique à la gestion utilisateur */}

        <form ref={formRef} onSubmit={save} noValidate>
          <AntForm
            form={searchForm}
            component="div"
            labelAlign="left"
            disabled={busy}
            onValuesChange={(changed, all) => {
              if ('CODSTE_SEARCH' in changed) {
                update('CODSTE', String(all.CODSTE_SEARCH ?? ''));
              }
              if ('lib_CODSTE_SEARCH' in changed) {
                update('RAISON', String(all.lib_CODSTE_SEARCH ?? ''));
              }
            }}
          >
            <Grid container spacing={0}>
              <Grid item xs={12} sm={6} md={4}>
                <AntForm.Item label="Code société" {...itemProps('CODSTE')}>
                  <SearchModal
                    form={searchForm}
                    Titre="Recherche Société"
                    label=""
                    name="CODSTE_SEARCH"
                    width={7}
                    Taille1="100%"
                    Taille2="100%"
                    notRequired
                    notremove
                    defaultCode={form.CODSTE}
                    defaultLibelle={form.RAISON}
                    data={societes.map((s, i) => ({ key: i, Code: s.CODSTE, Libelle: s.RAISON }))}
                    handleFunction={selectSocieteFromSearch}
                    Parametre={searchSocieteParam}
                    disabled={busy || editing}
                  />
                </AntForm.Item>
              </Grid>
            </Grid>

            <fieldset disabled={busy || formLocked} style={{ border: 0, padding: 0, margin: 0 }}>

              {/* Bloc 1 — Informations de base */}
              <fieldset className="bordure1">
                <legend><Typography color="#116599">1. Informations de base</Typography></legend>
                <Grid container spacing={0}>
                  <Grid item xs={12} sm={6} md={8}>
                    <AntForm.Item label="Raison sociale" {...itemProps('RAISON')} labelCol={{ span: 4, style: { whiteSpace: 'pre-wrap' as const } }}>
                      <AntInput
                        style={{ width: '100%' }}
                        value={form.RAISON}
                        onChange={(e) => update('RAISON', e.target.value)}
                        disabled={busy || formLocked}
                      />
                    </AntForm.Item>
                  </Grid>
                  <Grid item xs={12} sm={6} md={4}>
                    <AntForm.Item label="Statut" {...itemProps('STATUT')}>
                      <SearchModal form={searchForm} Titre="Recherche statut" label="" name="STATUT_SEARCH" width={7} Taille1="100%" Taille2="100%" notRequired notremove defaultCode={form.STATUT ?? ''} defaultLibelle={STATUT_OPTIONS.find((o) => o.value === form.STATUT)?.label ?? ''} data={STATUT_OPTIONS.map((o, i) => ({ key: i, Code: o.value, Libelle: o.label }))} handleFunction={(value: string) => update('STATUT', value)} Parametre={searchStatutParam} disabled={busy || formLocked} />
                    </AntForm.Item>
                  </Grid>
                  <Grid item xs={12} sm={6} md={4}>
                    <AntForm.Item label="Type de société" {...itemProps('TYPESTE')}>
                      <SearchModal form={searchForm} Titre="Recherche type de société" label="" name="TYPESTE_SEARCH" width={7} Taille1="100%" Taille2="100%" notRequired notremove defaultCode={form.TYPESTE ?? ''} defaultLibelle={typeSocieteOptions.find((o) => o.value === form.TYPESTE)?.label ?? ''} data={typeSocieteOptions.map((o, i) => ({ key: i, Code: o.value, Libelle: o.label }))} handleFunction={(value: string) => update('TYPESTE', value)} Parametre={searchTypeSocieteParam} disabled={busy || formLocked} />
                    </AntForm.Item>
                  </Grid>
                  <Grid item xs={12} sm={6} md={4}>
                    <AntForm.Item label="Format compte client" {...itemProps('formatCptCli')}>
                      <SearchModal
                        form={searchForm}
                        Titre="Recherche format compte client"
                        label=""
                        name="FORMATCPTCLI_SEARCH"
                        width={7}
                        Taille1="100%"
                        Taille2="100%"
                        notRequired
                        notremove
                        defaultCode={form.formatCptCli ?? ''}
                        defaultLibelle={formatCompteOptions.find((option) => option.value === form.formatCptCli)?.label ?? ''}
                        data={formatCompteOptions.map((option, index) => ({ key: index, Code: option.value, Libelle: option.label }))}
                        handleFunction={(value: string) => update('formatCptCli', String(value))}
                        disabled={busy || formLocked}
                      />
                    </AntForm.Item>
                  </Grid>
                  <Grid item xs={12} sm={6} md={4}>
                    <AntForm.Item label="Format compte GL" {...itemProps('formatCptGl')}>
                      <SearchModal
                        form={searchForm}
                        Titre="Recherche format compte GL"
                        label=""
                        name="FORMATCPTGL_SEARCH"
                        width={7}
                        Taille1="100%"
                        Taille2="100%"
                        notRequired
                        notremove
                        defaultCode={form.formatCptGl ?? ''}
                        defaultLibelle={formatCompteOptions.find((option) => option.value === form.formatCptGl)?.label ?? ''}
                        data={formatCompteOptions.map((option, index) => ({ key: index, Code: option.value, Libelle: option.label }))}
                        handleFunction={(value: string) => update('formatCptGl', String(value))}
                        disabled={busy || formLocked}
                      />
                    </AntForm.Item>
                  </Grid>
                  <Grid item xs={12} sm={6} md={12}>
                    <AntForm.Item label="Autoriser la generation de format de compte libre" {...itemProps('AUTFORMATLIBRE')}>
                      <Checkbox
                        checked={form.AUTFORMATLIBRE ?? false}
                        onChange={(e) => update('AUTFORMATLIBRE', e.target.checked)}
                        disabled={busy || formLocked}
                      >
                        Oui
                      </Checkbox>
                    </AntForm.Item>
                  </Grid>
                </Grid>
              </fieldset>

              {/* Bloc 2 — Activités, secteur et informations financières */}
              <fieldset className="bordure1">
                <legend><Typography color="#116599">2. Activités, secteur et informations financières</Typography></legend>
                <Grid container spacing={0}>
                  <Grid item xs={12} sm={6} md={4}>
                    <AntForm.Item label="Activité primaire" {...itemProps('ACTIVITEP')}>
                      <SearchModal
                        form={searchForm}
                        Titre="Recherche activité"
                        label=""
                        name="ACTIVITEP_SEARCH"
                        width={7}
                        Taille1="100%"
                        Taille2="100%"
                        notRequired
                        notremove
                        defaultCode={form.ACTIVITEP ?? ''}
                        defaultLibelle={activiteOptions.find((o) => o.value === form.ACTIVITEP)?.label ?? ''}
                        data={activiteOptions.map((option, index) => ({ key: index, Code: option.value, Libelle: option.label }))}
                        handleFunction={selectActivitePFromSearch}
                        Parametre={searchActiviteParam}
                      />
                    </AntForm.Item>
                  </Grid>
                  <Grid item xs={12} sm={6} md={4}>
                    <AntForm.Item label="Activité secondaire" {...itemProps('ACTIVITEA')}>
                      <SearchModal
                        form={searchForm}
                        Titre="Recherche activité"
                        label=""
                        name="ACTIVITEA_SEARCH"
                        width={7}
                        Taille1="100%"
                        Taille2="100%"
                        notRequired
                        notremove
                        defaultCode={form.ACTIVITEA ?? ''}
                        defaultLibelle={activiteOptions.find((o) => o.value === form.ACTIVITEA)?.label ?? ''}
                        data={activiteOptions.map((option, index) => ({ key: index, Code: option.value, Libelle: option.label }))}
                        handleFunction={selectActiviteAFromSearch}
                        Parametre={searchActiviteParam}
                      />
                    </AntForm.Item>
                  </Grid>
                  <Grid item xs={12} sm={6} md={4}>
                    <AntForm.Item label="Secteur" {...itemProps('SECTEUR')}>
                      <SearchModal
                        form={searchForm}
                        Titre="Recherche secteur"
                        label=""
                        name="SECTEUR_SEARCH"
                        width={7}
                        Taille1="100%"
                        Taille2="100%"
                        notRequired
                        notremove
                        defaultCode={form.SECTEUR ?? ''}
                        defaultLibelle={secteurOptions.find((o) => o.value === form.SECTEUR)?.label ?? ''}
                        data={secteurOptions.map((option, index) => ({ key: index, Code: option.value, Libelle: option.label }))}
                        handleFunction={selectSecteurFromSearch}
                        Parametre={searchSecteurParam}
                      />
                    </AntForm.Item>
                  </Grid>
                  <Grid item xs={12} sm={6} md={4}>
                    <AntForm.Item label="Catégorie EMF" {...itemProps('categoEmf')}>
                      <SearchModal
                        form={searchForm}
                        Titre="Recherche catégorie EMF"
                        label=""
                        name="CATEGOEMF_SEARCH"
                        width={7}
                        Taille1="100%"
                        Taille2="100%"
                        notRequired
                        notremove
                        defaultCode={form.categoEmf ? String(form.categoEmf) : ''}
                        defaultLibelle={categorieOptions.find((o) => o.value === form.categoEmf)?.label ?? ''}
                        data={categorieOptions.map((option, index) => ({ key: index, Code: String(option.value), Libelle: option.label }))}
                        handleFunction={selectCategorieEmfFromSearch}
                        Parametre={searchCategorieEmfParam}
                      />
                    </AntForm.Item>
                  </Grid>
                  <Grid item xs={12} sm={6} md={4}>
                    <AntForm.Item label="Chiffre d'affaires" {...itemProps('CHIFFREA')}>
                      <InputNumber
                        style={{ width: '100%' }}
                        min={0}
                        addonBefore={form.coddev ?? 'XAF'}
                        formatter={(value) => `${value}`.replace(/\B(?=(\d{3})+(?!\d))/g, ' ')}
                        value={form.CHIFFREA ?? undefined}
                        onChange={(value) => update('CHIFFREA', value === null || value === undefined ? null : Number(value))}
                      />
                    </AntForm.Item>
                  </Grid>
                  <Grid item xs={12} sm={6} md={4}>
                    <AntForm.Item label="Date de création" {...itemProps('DATNAI')}>
                      <DatePicker
                        format="DD/MM/YYYY"

                        style={{ width: '100%' }}
                        value={datnai && datnai.isValid() ? datnai : null}
                        onChange={(date) => update('DATNAI', date ? date.format('YYYY-MM-DD') : null)}
                      />
                    </AntForm.Item>
                  </Grid>
                </Grid>
              </fieldset>

              {/* Bloc 3 — Coordonnées et informations légales */}
              <fieldset className="bordure1">
                <legend><Typography color="#116599">3. Coordonnées et informations légales</Typography></legend>
                <Grid container spacing={0}>
                  <Grid item xs={12} sm={6} md={4}>
                    <AntForm.Item label="Boîte postale" {...itemProps('BP')}>
                      <AntInput
                        style={{ width: '100%' }}
                        value={form.BP ?? ''}
                        onChange={(e) => update('BP', e.target.value)}

                      />
                    </AntForm.Item>
                  </Grid>
                  <Grid item xs={12} sm={6} md={4}>
                    <AntForm.Item label="Téléphone" {...itemProps('TEL')}>
                      <AntInput
                        style={{ width: '100%' }}
                        value={form.TEL ?? ''}
                        onChange={(e) => update('TEL', e.target.value)}

                        addonBefore={<Phone fontSize="small" color="action" />}
                      />
                    </AntForm.Item>
                  </Grid>
                  <Grid item xs={12} sm={6} md={4}>
                    <AntForm.Item label="Fax" {...itemProps('FAX')}>
                      <AntInput
                        style={{ width: '100%' }}
                        value={form.FAX ?? ''}
                        onChange={(e) => update('FAX', e.target.value)}

                      />
                    </AntForm.Item>
                  </Grid>
                  <Grid item xs={12} sm={6} md={4}>
                    <AntForm.Item label="Email" {...itemProps('EMAIL')}>
                      <AntInput
                        style={{ width: '100%' }}
                        value={form.EMAIL ?? ''}
                        onChange={(e) => update('EMAIL', e.target.value)}

                        addonBefore={<Email fontSize="small" color="action" />}
                      />
                    </AntForm.Item>
                  </Grid>
                  <Grid item xs={12} sm={6} md={8}>
                    <AntForm.Item label="Localisation" {...itemProps('LOCAL')} labelCol={{ span: 4, style: { whiteSpace: 'pre-wrap' as const } }}>
                      <AntInput
                        style={{ width: '100%' }}
                        value={form.LOCAL ?? ''}
                        onChange={(e) => update('LOCAL', e.target.value)}

                        addonBefore={<LocationOn fontSize="small" color="action" />}
                      />
                    </AntForm.Item>
                  </Grid>
                  <Grid item xs={12} sm={6} md={4}>
                    <AntForm.Item label="Contribuable" {...itemProps('CONTRIB')}>
                      <AntInput
                        style={{ width: '100%' }}
                        value={form.CONTRIB ?? ''}
                        onChange={(e) => update('CONTRIB', e.target.value)}

                      />
                    </AntForm.Item>
                  </Grid>
                  <Grid item xs={12} sm={6} md={4}>
                    <AntForm.Item label="Registre de commerce" {...itemProps('REGISTRE')}>
                      <AntInput
                        style={{ width: '100%' }}
                        value={form.REGISTRE ?? ''}
                        onChange={(e) => update('REGISTRE', e.target.value)}

                      />
                    </AntForm.Item>
                  </Grid>
                  <Grid item xs={12} sm={6} md={4}>
                    <AntForm.Item label="Pays" {...itemProps('codpays')}>
                      <SearchModal form={searchForm} Titre="Recherche pays" label="" name="PAYS_SEARCH" width={7} Taille1="100%" Taille2="100%" notRequired notremove defaultCode={form.codpays ?? ''} defaultLibelle={PAYS_OPTIONS.find((o) => o.value === form.codpays)?.label ?? ''} data={PAYS_OPTIONS.map((o, i) => ({ key: i, Code: o.value, Libelle: o.label }))} handleFunction={(value: string) => update('codpays', value)} Parametre={searchPaysParam} />
                    </AntForm.Item>
                  </Grid>
                  <Grid item xs={12} sm={6} md={4}>
                    <AntForm.Item label="Devise" {...itemProps('coddev')}>
                      <SearchModal form={searchForm} Titre="Recherche devise" label="" name="DEVISE_SEARCH" width={7} Taille1="100%" Taille2="100%" notRequired notremove defaultCode={form.coddev ?? ''} defaultLibelle={DEVISE_OPTIONS.find((o) => o.value === form.coddev)?.label ?? ''} data={DEVISE_OPTIONS.map((o, i) => ({ key: i, Code: o.value, Libelle: o.label }))} handleFunction={(value: string) => update('coddev', value)} Parametre={searchDeviseParam} />
                    </AntForm.Item>
                  </Grid>
                </Grid>
              </fieldset>
            </fieldset>

            {!formLocked && Object.keys(errors).length > 0 && (
              <Typography variant="body2" sx={{ color: '#a41b1b', px: 1 }}>
                Veuillez renseigner les champs obligatoires avant d'enregistrer.
              </Typography>
            )}
          </AntForm>
        </form>

      </Box>
    </div>
  );
}